// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@stanion/App/Store/Reducers";
import React, { FC, useState } from "react";
import { css } from "styled-components";

type Props = {
    alphabet: LetterDetails[];
    prefix?: string;
    extendedStyles?: AlphabetNavigationStyles;
    onLetterClick?: (letter: string) => void;
};

export interface LetterDetails {
    letter: string;
    linkable: boolean;
}

export interface AlphabetNavigationStyles {
    container?: InjectableCss;
    navigationItem?: InjectableCss;
    navigationLink?: LinkProps;
    navigationLinkText?: TypographyPresentationProps;
    navigationTextEmpty?: TypographyPresentationProps;
}

export const alphabetNavigationStyles: AlphabetNavigationStyles = {
    container: {
        css: css`
            text-transform: uppercase;
            justify-items: center;
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
        `,
    },
    navigationItem: {
        css: css`
            margin: 0;
            display: inline-block;
            flex: 0 0 30px;
        `,
    },
    navigationLink: {
        css: css`
            display: block;
        `,
        typographyProps: {
            transform: "uppercase",
            variant: "headerTertiary",
            forwardAs: "span",
        },
    },
    navigationTextEmpty: {
        transform: "uppercase",
        variant: "headerTertiary",
        forwardAs: "span",
        color: "text.disabled",
    },
};

const StyledSpan = getStyledWrapper("span");

const AlphabetNavigation: FC<Props> = (props: Props) => {
    const [styles] = useState(() => mergeToNew(alphabetNavigationStyles, props.extendedStyles));
    const [alphabet] = useState(() => props.alphabet);
    // XNG-Changes
    const isMobile = useAppSelector(state => (state as CustomAppState).xCommon.isMobileScreen);
    const { headerHeight } = useAppSelector(state => ({
        headerHeight: (state as CustomAppState).xHeader?.headerHeight,
    }));

    const handleClickOfLetter = (letter: string) => {
        props.onLetterClick?.(letter);
    };
    // XNG-Changes: scroll to letter - headerHeight
    const createHandleClickOfLetter = (letter: string) => () => {
        const letterElement = document.getElementById(`${props.prefix || ""}letter-${letter}`);
        if (letterElement) {
            letterElement.parentElement &&
                window.scrollTo({
                    top: isMobile
                        ? letterElement.parentElement.offsetTop
                        : letterElement.parentElement.offsetTop - parseInt(headerHeight!, 10) - 10,
                    behavior: "smooth",
                });
        }
        return handleClickOfLetter(letter);
    };

    if (alphabet.length <= 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="alphabetNavigation">
            {alphabet.map(alphabetItem => (
                <StyledSpan
                    {...styles.navigationItem}
                    key={alphabetItem.letter}
                    data-test-selector="alphabetNavigationItemLetter"
                >
                    {alphabetItem.linkable ? (
                        <Link {...styles.navigationLink} onClick={createHandleClickOfLetter(alphabetItem.letter)}>
                            <u>{alphabetItem.letter}</u>
                        </Link>
                    ) : (
                        <Typography {...styles.navigationTextEmpty}>{alphabetItem.letter}</Typography>
                    )}
                </StyledSpan>
            ))}
        </StyledWrapper>
    );
};

export default AlphabetNavigation;
